import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { API_BASE_URL } from '../../constants';
import './UploadProjectModal.css';

const RANDOM_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f59e0b', '#10b981', '#06b6d4', '#3b82f6'
];

const SIZE_LIMITS = {
  image: 5 * 1024 * 1024,
  step: 50 * 1024 * 1024,
  xml: 10 * 1024 * 1024,
};

function generateProjectId() {
  return Array.from({ length: 12 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join('');
}

function UploadProjectModal({ onClose, onSuccess, currentUser }) {
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [stepFile, setStepFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelection = (event, category) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    const sizeLimit = SIZE_LIMITS[category];
    const oversize = files.find((file) => file.size > sizeLimit);
    if (oversize) {
      setError(`${oversize.name} exceeds the ${category.toUpperCase()} limit (${Math.round(sizeLimit / (1024 * 1024))}MB)`);
      event.target.value = '';
      return;
    }

    setError('');

    if (category === 'image') {
      setImageFiles(files);
    } else if (category === 'step') {
      setStepFile(files[0]);
    } else if (category === 'xml') {
      setXmlFile(files[0]);
    }

    event.target.value = '';
  };

  const removeImageAt = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    if (!stepFile) {
      setError('STEP file is required');
      return;
    }

    if (!xmlFile) {
      setError('XML file is required');
      return;
    }

    const priceVal = parseFloat(price);
    if (price && priceVal > 0 && priceVal < 0.50) {
      setError('Minimum price is $0.50 (or leave empty/0 for free)');
      return;
    }

    setUploading(true);

    try {
      const projectId = generateProjectId();
      const userId = currentUser.uid;
      const folderName = `${userId}-${projectName.replace(/[^a-zA-Z0-9]/g, '_')}-${projectId}`;
      
      // Random color for placeholder if no image
      const imageColor = RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)];

      console.log('Starting upload process...');
      console.log('Project ID:', projectId);
      console.log('Folder name:', folderName);

      // Step 1: Upload files to GCS via backend
      const formData = new FormData();
      formData.append('userId', userId);
      formData.append('projectName', projectName);
      formData.append('projectId', projectId);
      
      if (imageFiles.length) {
        imageFiles.forEach((file) => formData.append('image', file));
      }
      formData.append('step', stepFile);
      formData.append('xml', xmlFile);

      console.log('Uploading files to GCS...');
      const token = await auth.currentUser?.getIdToken();
      const uploadResponse = await fetch(`${API_BASE_URL}/api/upload-project`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const uploadResult = await uploadResponse.json();
      console.log('✓ Files uploaded to GCS:', uploadResult);

      // Step 2: Save metadata to Firestore with actual GCS paths
      const projectData = {
        projectId,
        name: projectName,
        description: description || '',
        price: price ? parseFloat(parseFloat(price).toFixed(2)) : 0, // store in dollars
        currency: 'usd',
        ownerId: userId,
        ownerName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous',
        ownerEmail: currentUser.email || '',
        folderName: uploadResult.folderName,
        imageColor,
        imageUrls: uploadResult.imageUrls || (uploadResult.imageUrl ? [uploadResult.imageUrl] : []),
        imageFileNames: uploadResult.imageFileNames || (uploadResult.imageFileName ? [uploadResult.imageFileName] : []),
        imageUrl: uploadResult.imageUrl || null,
        imageFileName: uploadResult.imageFileName || null,
        stepFilePath: uploadResult.stepFilePath,
        stepFileName: uploadResult.stepFileName,
        xmlFilePath: uploadResult.xmlFilePath,
        xmlFileName: uploadResult.xmlFileName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      console.log('Saving metadata to Firestore...');
      await addDoc(collection(db, 'marketplace-projects'), projectData);
      console.log('✓ Project created successfully');
      
      onSuccess();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload project. Please ensure backend server is running.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upload-modal__header">
          <h2>Upload New Project</h2>
          <button className="upload-modal__close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="upload-modal__form">
          {error && (
            <div className="upload-modal__error">{error}</div>
          )}

          <div className="upload-modal__field">
            <label>Project Name *</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="My Robot Behavior Tree"
              disabled={uploading}
              required
            />
          </div>

          <div className="upload-modal__field">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your project..."
              rows={3}
              disabled={uploading}
            />
          </div>

          <div className="upload-modal__field">
            <label>Price (USD) — leave empty or 0 for free (min $0.50)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              disabled={uploading}
            />
          </div>

          <div className="upload-modal__field">
            <label>Project Images (optional)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => handleFileSelection(event, 'image')}
              disabled={uploading}
            />
            {imageFiles.length ? (
              <ul className="upload-modal__file-list">
                {imageFiles.map((file, index) => (
                  <li key={file.name + index} className="upload-modal__file-list-item">
                    <span>✓ {file.name}</span>
                    <button
                      type="button"
                      className="upload-modal__file-clear"
                      onClick={() => removeImageAt(index)}
                      disabled={uploading}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="upload-modal__filename upload-modal__filename--missing">No files selected</span>
            )}
          </div>

          <div className="upload-modal__field">
            <label>STEP File *</label>
            <input
              type="file"
              accept=".step,.stp"
              multiple
              onChange={(event) => handleFileSelection(event, 'step')}
              disabled={uploading}
            />
            {stepFile ? (
              <span className="upload-modal__filename">✓ {stepFile.name}</span>
            ) : (
              <span className="upload-modal__filename upload-modal__filename--missing">Required</span>
            )}
          </div>

          <div className="upload-modal__field">
            <label>XML File *</label>
            <input
              type="file"
              accept=".xml"
              multiple
              onChange={(event) => handleFileSelection(event, 'xml')}
              disabled={uploading}
            />
            {xmlFile ? (
              <span className="upload-modal__filename">✓ {xmlFile.name}</span>
            ) : (
              <span className="upload-modal__filename upload-modal__filename--missing">Required</span>
            )}
          </div>

          <div className="upload-modal__actions">
            <button
              type="button"
              className="upload-modal__btn upload-modal__btn--cancel"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="upload-modal__btn upload-modal__btn--submit"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UploadProjectModal;
